import React, { useEffect, useState } from 'react'
import { compareDesc } from 'date-fns'
import {
    Card as MuiCard,
    CardContent,
    Typography,
    Box,
    FormControlLabel,
    Checkbox,
    Stack,
    Tooltip,
    IconButton,
    Divider,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { spacing } from '@mui/system'

import IssueApi from '../../../../api/IssueApi'
import { IssueEntity } from '../../../../types/IssueEntity'
import { TableRow } from './TableRow'
import { MCIssueEntity } from '../../../../types/MCEntity'
import { AddLink } from '@mui/icons-material'
import { hasSourceOfType } from '../../../../components/utils/calculateIssueStatus'

const Card = styled(MuiCard)(spacing)

export type ExtendedIssue = IssueEntity & { matchedTo: string | null }

type FLIssueGridComponent = React.FC<{
    selectedMCIssue: MCIssueEntity | null
    setMCIssues: React.Dispatch<React.SetStateAction<MCIssueEntity[]>>
    onArchiveMcIssue: (mcIssueToArchive: MCIssueEntity) => Promise<void>
    markAsConvertible: (mcIssueToMarkAsConvertible: MCIssueEntity) => Promise<void>
}>

export const FLIssuesGrid: FLIssueGridComponent = ({ selectedMCIssue, onArchiveMcIssue, markAsConvertible }) => {
    const [issues, setIssues] = useState<ExtendedIssue[]>([])
    const [showMatchedIssues, setShowMatchedIssues] = useState(false)
    const issueApi = new IssueApi()

    const fetchIssues = async () => {
        if (selectedMCIssue) {
            const fetchedIssues = (await issueApi.findByShareId(selectedMCIssue.shareId)) as ExtendedIssue[]
            const filteredIssues = fetchedIssues.reduce((existingIssues: ExtendedIssue[], issue: ExtendedIssue) => {
                if (hasSourceOfType(issue, 'Cancellation')) return existingIssues

                const issueAlreadyExists = existingIssues.find((existingIssue) => existingIssue.id === issue.id)
                if (!issueAlreadyExists) {
                    existingIssues.push(issue)
                }
                return existingIssues
            }, [])

            const sortedIssues = filteredIssues.sort((a, b) => {
                const dateA = a.exRightsDate ? a.exRightsDate : a.subEndDate ? a.subEndDate : a.publicationDate!
                const dateB = b.exRightsDate ? b.exRightsDate : b.subEndDate ? b.subEndDate : b.publicationDate!
                return compareDesc(new Date(dateA), new Date(dateB))
            })
            setIssues(sortedIssues)
        }
    }

    useEffect(() => {
        void fetchIssues()
    }, [selectedMCIssue?.id])

    const connectToMCToFLIssue = async (issue: ExtendedIssue) => {
        if (selectedMCIssue?.id && selectedMCIssue.matchedTo !== issue.id) {
            const response = await issueApi.matchIssueToInstrumentHistory(issue.id, selectedMCIssue.id)

            if (response) {
                void fetchIssues()
            }
        }
    }

    if (!selectedMCIssue) {
        return null
    }

    return (
        <Card mb={6}>
            <CardContent sx={{ paddingTop: 0 }}>
                <h3>{selectedMCIssue.name}</h3>
                <Stack spacing={4} direction={'row'}>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip title='Arkivera MC emission'>
                            <IconButton
                                sx={{
                                    width: 30, // Adjust the width for your desired size
                                    height: 30, // Adjust the height for your desired size
                                    borderRadius: '50%', // Makes the button round
                                    backgroundColor: 'primary.main', // Optional: Set a background color for better visibility
                                    border: '2px solid', // Adds a border
                                    borderColor: 'primary.main', // Set border color with main variant
                                    color: 'white', // Set icon color for better contrast
                                    '&:hover': {
                                        backgroundColor: 'primary.dark', // Darker color on hover
                                        borderColor: 'primary.dark', // Darker border color on hover
                                    },
                                }}
                                onClick={() => {
                                    if (selectedMCIssue) {
                                        void onArchiveMcIssue(selectedMCIssue)
                                    }
                                }}
                            >
                                <AddLink sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                        <Typography>Arkivera</Typography>
                    </Stack>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip title='Markera MC emission som konvertibel'>
                            <IconButton
                                sx={{
                                    width: 30, // Adjust the width for your desired size
                                    height: 30, // Adjust the height for your desired size
                                    borderRadius: '50%', // Makes the button round
                                    backgroundColor: 'primary.main', // Optional: Set a background color for better visibility
                                    border: '2px solid', // Adds a border
                                    borderColor: 'primary.main', // Set border color with main variant
                                    color: 'white', // Set icon color for better contrast
                                    '&:hover': {
                                        backgroundColor: 'primary.dark', // Darker color on hover
                                        borderColor: 'primary.dark', // Darker border color on hover
                                    },
                                }}
                                onClick={() => {
                                    if (selectedMCIssue) {
                                        void markAsConvertible(selectedMCIssue)
                                    }
                                }}
                            >
                                <AddLink sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                        <Typography>Konvertibel</Typography>
                    </Stack>
                </Stack>

                <Divider sx={{ marginY: 2 }} />
                <strong>Kommentar </strong>
                {selectedMCIssue.actionComment}
                <Divider sx={{ marginY: 2 }} />

                {issues.length > 0 ? (
                    <>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showMatchedIssues}
                                        onChange={() => setShowMatchedIssues((prev) => !prev)}
                                        color='primary'
                                        disableRipple
                                    />
                                }
                                label='Visa avstämda'
                            />
                        </Box>
                        <table width='100%'>
                            <thead>
                                <tr
                                    style={{
                                        height: '52px',
                                    }}
                                >
                                    <td>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            Emission
                                        </Typography>
                                    </td>
                                    <td style={{ minWidth: '50px' }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            Kurs
                                        </Typography>
                                    </td>
                                    <td style={{ minWidth: '70px' }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            Nya:Gamla
                                        </Typography>
                                    </td>
                                    <td style={{ minWidth: '76px' }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            Antal aktier
                                        </Typography>
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {issues
                                    .filter((issue) => (showMatchedIssues ? showMatchedIssues : !issue.matchedTo))
                                    .map((issue) => (
                                        <TableRow
                                            key={`${issue.id}-${issue.matchedTo}`}
                                            issue={issue}
                                            onConnect={connectToMCToFLIssue}
                                        />
                                    ))}
                            </tbody>
                        </table>
                    </>
                ) : null}
            </CardContent>
        </Card>
    )
}

export default FLIssuesGrid
